import { config } from "../../config/config";
import { authHeaders, handleError } from "./_helpers";
import {
  gatewayProviderCapabilitiesResponseSchema,
  type GatewayProviderCapabilities,
} from "../../utils/zod/gatewayProviders";

export type GatewayTransactionStatus =
  | "PENDING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED"
  | "DISPUTED";

export type DisputeStatus = "OPEN" | "UNDER_REVIEW" | "WON" | "LOST";

export interface GatewayTransactionClient {
  publicId: string;
  firstName: string;
  lastName?: string | null;
  businessName?: string | null;
}

export interface GatewayTransactionPaymentMethod {
  name: string;
  key: string;
}

export interface GatewayTransactionPlanPayment {
  publicId: string;
  status: string;
  amount: number;
  plans: string[];
}

export interface GatewayCreatedBy {
  source: "CLIENT_APP" | "STAFF" | "UNKNOWN";
  email: string | null;
  name: string | null;
}

export interface GatewayTransaction {
  publicId: string;
  provider: string;
  status: GatewayTransactionStatus;
  checkoutUrl: string | null;
  externalId: string;
  externalPaymentId: string | null;
  amount: number;
  description: string;
  /** Por qué falló, en castellano. Null mientras no haya fallado. */
  failureReason: string | null;
  /** El código del rechazo, sin traducir. Null si la pasarela no lo informa. */
  statusDetail: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  client: GatewayTransactionClient;
  paymentMethod: GatewayTransactionPaymentMethod;
  planPayment: GatewayTransactionPlanPayment | null;
  createdBy: GatewayCreatedBy;
  dispute: {
    publicId: string;
    status: DisputeStatus;
    reason: string | null;
    amount: number | null;
    source: string;
    documentationDeadline: string | null;
    createdAt: string;
  } | null;
}

export interface GatewayTransactionsPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface GatewayTransactionsSummary {
  /** Mes resuelto ("YYYY-MM") o ALL_MONTHS. */
  month: string;
  pending: { count: number; amount: number };
  completed: { count: number; amount: number };
  failed: { count: number };
  disputed: { count: number; amount: number };
}

export interface GatewayTransactionsResponse {
  transactions: GatewayTransaction[];
  /** Agregados del gym en el mes: ignoran el filtro de estado. */
  summary: GatewayTransactionsSummary;
  pagination: GatewayTransactionsPagination;
}

/** Valor del filtro de mes que desactiva el acote temporal. */
export const ALL_MONTHS = "all";

export interface GetGatewayTransactionsParams {
  status?: GatewayTransactionStatus | "ALL";
  provider?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  month?: string;
}

export const getGatewayTransactionsRequest = async (
  token: string,
  params: GetGatewayTransactionsParams = {},
): Promise<GatewayTransactionsResponse> => {
  const searchParams = new URLSearchParams();

  if (params.status !== undefined && params.status !== "ALL")
    searchParams.set("status", params.status);
  if (params.provider) searchParams.set("provider", params.provider);
  if (params.page !== undefined) searchParams.set("page", String(params.page));
  if (params.pageSize !== undefined)
    searchParams.set("pageSize", String(params.pageSize));
  if (params.search !== undefined && params.search !== "")
    searchParams.set("search", params.search);
  if (params.month) searchParams.set("month", params.month);

  const query = searchParams.toString();
  const url = `${config.API_URL}/gateway-transactions${query ? `?${query}` : ""}`;

  const response = await fetch(url, {
    method: "GET",
    headers: authHeaders(token),
  });

  if (!response.ok)
    return handleError(response, "Error al obtener las transacciones");

  return response.json();
};

export interface TransactionSyncPreviewResult {
  pendingChecked: number;
  staleChecked: number;
  changes: Array<{
    transactionPublicId: string;
    clientName: string;
    description: string;
    amount: number;
    currentStatus: string;
    newStatus: string;
  }>;
  toExpire: number;
  errors: number;
}

export const previewSyncTransactionsRequest = async (
  token: string,
): Promise<TransactionSyncPreviewResult> => {
  const response = await fetch(
    `${config.API_URL}/gateway-transactions/sync/preview`,
    {
      method: "POST",
      headers: authHeaders(token),
    },
  );

  if (!response.ok)
    return handleError(
      response,
      "Error al obtener vista previa de sincronizacion",
    );

  return response.json();
};

export async function syncTransactionsRequest(
  token: string,
): Promise<{ reconciled: number; expired: number; errors: number }> {
  const response = await fetch(`${config.API_URL}/gateway-transactions/sync`, {
    method: "POST",
    headers: authHeaders(token),
  });
  if (!response.ok)
    return handleError(response, "Error al sincronizar transacciones");
  return response.json();
}

export async function updateDisputeStatusRequest(
  token: string,
  disputePublicId: string,
  data: { status: DisputeStatus; notes?: string },
): Promise<{
  publicId: string;
  status: DisputeStatus;
  resolvedAt: string | null;
}> {
  const response = await fetch(
    `${config.API_URL}/gateway-disputes/${disputePublicId}`,
    {
      method: "PATCH",
      headers: authHeaders(token),
      body: JSON.stringify(data),
    },
  );
  if (!response.ok)
    return handleError(response, "Error al actualizar la disputa");
  return response.json();
}

export async function resendTransactionEmailRequest(
  token: string,
  publicId: string,
): Promise<{ message: string }> {
  const response = await fetch(
    `${config.API_URL}/gateway-transactions/${publicId}/resend-email`,
    { method: "POST", headers: authHeaders(token) },
  );
  if (!response.ok) return handleError(response, "Error al reenviar el email");
  return response.json();
}

/**
 * Que soporta cada pasarela de la plataforma.
 *
 * Si esto falla, quien llama tiene que seguir funcionando con los valores por
 * defecto del cliente: un gym que no puede elegir pasarela es un gym que no
 * puede cobrar.
 */
export async function getGatewayProviderCapabilitiesRequest(
  token: string,
): Promise<GatewayProviderCapabilities[]> {
  const response = await fetch(`${config.API_URL}/v1/gateway/providers`, {
    headers: authHeaders(token),
  });

  if (!response.ok) {
    return handleError(response, "Error al obtener las pasarelas disponibles");
  }

  const body: unknown = await response.json();
  return gatewayProviderCapabilitiesResponseSchema.parse(body).providers;
}
